import React, { Fragment, useEffect, useState } from 'react'
import { Edit as EditIcon } from '@mui/icons-material'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { DispatchOtDetail, WorkOrderDispatchDto } from './entities/DispatchOt'
import { useApi } from '../../hooks/useApi'
import { usePagination } from '../../hooks/usePagination'
import { Status } from '../../hooks/useFetchState'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { generateTableColumnsDispatchOt } from './config'
import { getDispatchOts, postDispatchWorkOrder } from './config/api'
import { OtDescription } from './components/OtDescription'
import { DispatchOtButtonBar } from './components/DispatchOtButtonBar'
import { GenericTableV2 } from '../../components/_EOS/GenericTable'
import { Filter } from '../../components/_WMS/FilterComponent'
import { DispatchOtNewSearch } from './search/DispatchOtNewSearch'
import { useTypeInventoryIncident } from '../../hooks/useTypeInventoryIncident'

import { PrintManifestModal } from '../../components/DispatchOt/PrintManifestModal'
import { DispatchConfirmModal } from '../../components/DispatchOt/DispatchConfirmModal'
import { BreakStockModal } from '../../components/DispatchOt/BreakStockModal'
import { DeclareErrorModal } from '../../components/DispatchOt/DeclareErrorModal'
import { UnifiedDispatchModalWrapper } from '../../components/DispatchOt/UnifiedDispatchModalWrapper'
import { useModal } from '../../hooks/useModal'
import { useUser } from '../../hooks/useUser'
import { getStyleRow, getExpiredStatusToday } from '../../utils/colors'
import { useProductOrigins } from '../../hooks'

interface PrintModalState {
  workOrderId: number
  docPDF: string
  isLoading: boolean
}

interface DispatchModalState {
  workOrderId: number
  isLoading: boolean
}

interface BreakStockModalState {
  // Estado simple, solo necesita saber si está abierto
}

interface DeclareErrorModalState {
  workOrderId: number
  userId: number
}

interface UnifiedDispatchModalState {
  selectedRow: any
  workOrderId: number
  userId: number
}

interface DispatchOtNewPageProps {}

export const DispatchOtNewPage: React.FC<DispatchOtNewPageProps> = () => {
  const [currentFilters, setCurrentFilters] = useState<Filter[]>([])
  const [searchKey, setSearchKey] = useState(0)
  const { state, callApi, setIdle } = useApi<WorkOrderDispatchDto>()
  const { state: dispatchState, callApi: dispatchApi } = useApi()

  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()

  const {
    // getUserData,
    userDataState: { userData }
  } = useUser()

  // Hooks para los modales
  const printModal = useModal<PrintModalState>(
    'Imprimir Manifiesto',
    false,
    'lg'
  )
  const dispatchModal = useModal<DispatchModalState>(
    'Despachar OT',
    false,
    'xs'
  )
  const breakStockModal = useModal<BreakStockModalState>(
    'Quiebre de Stock',
    false,
    'sm'
  )
  const declareErrorModal = useModal<DeclareErrorModalState>(
    'Declarar Error',
    false,
    'md'
  )
  const unifiedDispatchModal = useModal<UnifiedDispatchModalState>(
    'Reportar Incidente',
    false,
    'lg'
  )

  // Hook para tipos de incidente
  const {
    typeInventoryIncidentState: { typeInventoryIncident },
    getTypeInventoryIncident
  } = useTypeInventoryIncident()

  // Hook para product origins (para colores FEFO)
  const {
    productOriginsState: { productOrigins },
    getProductOrigins
  } = useProductOrigins()

  const pagination = usePagination(20)

  const tableColumns = generateTableColumnsDispatchOt()

  const [orderBy, setOrderBy] = useState<{ field: keyof DispatchOtDetail, direction: 'asc' | 'desc' }>({
    field: 'sku',
    direction: 'asc'
  })

  const handleSorting = (field: keyof DispatchOtDetail, direction: 'asc' | 'desc') => {
    setOrderBy({ field, direction })
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    requestDispatchOts(currentFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const requestDispatchOts = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    callApi(getDispatchOts(filters, page, pageSize, orderBy))
      .catch((err) => {
        console.error('❌ DispatchOts API Error:', err)
        openSnackBar(
          err || 'Ha ocurrido un error consultando la información',
          false
        )
      })
  }

  // Efecto para manejar la respuesta del despacho
  useEffect(() => {
    if (dispatchState.status === Status.Success) {
      openSnackBar(`Se ha despachado la OT N° ${state.data?.id}`, true)
      handleClearSearch()
      dispatchModal.closeModal()
    } else if (dispatchState.status === Status.Error) {
      openSnackBar(
        `Ha ocurrido un error al despachar OT N° ${state.data?.id}`,
        false
      )
      dispatchModal.closeModal()
    }
  }, [dispatchState.status])

  // Acciones para la tabla
  const handleDispatchIncident = (
    _id: string | number,
    row: DispatchOtDetail
  ) => {
    // Obtener workOrderId del resultado de la búsqueda API
    const workOrderId = state.data?.id || 0

    // Abrir modal unificado
    unifiedDispatchModal.openModal('lg', {
      selectedRow: row,
      workOrderId: workOrderId,
      userId: userData?.userId || 0
    })
  }

  // Función para imprimir manifiesto
  const handlePrintDispatchManifest = (workOrderId: number) => {
    printModal.openModal('lg', { workOrderId, docPDF: '', isLoading: true })
  }

  // Función para despachar OT
  const handleDispatchOT = (workOrderId: number) => {
    dispatchApi(postDispatchWorkOrder(workOrderId, userData?.userId || 0))
  }

  // Función para manejar la búsqueda
  const handleSearch = (filters: Filter[]) => {
    setCurrentFilters(filters)
    // Realizar búsqueda
    requestDispatchOts(filters, 0, pagination.pageSize)
  }

  // Función para limpiar la búsqueda
  const handleClearSearch = () => {
    setCurrentFilters([])
    setSearchKey(prev => prev + 1)
    setIdle()
  }

  // Función para abrir modal de declarar error
  const handleOpenDeclareError = () => {
    declareErrorModal.openModal('md', {
      workOrderId: state.data?.id || 0,
      userId: userData?.userId || 0
    })
  }

  // Función para abrir modal de despacho
  const handleOpenDispatch = () => {
    dispatchModal.openModal('xs', {
      workOrderId: state.data?.id || 0,
      isLoading: false
    })
  }

  // Función para refrescar datos después de operaciones
  const handleGetDispatchOT = () => {
    if (currentFilters.length > 0) {
      requestDispatchOts(currentFilters, pagination.page, pagination.pageSize)
    }
  }

  // Cargar tipos de incidente al montar el componente
  useEffect(() => {
    getTypeInventoryIncident()
  }, [])

  // Cargar product origins al montar el componente (para colores FEFO)
  useEffect(() => {
    if (productOrigins?.length === 0) {
      getProductOrigins()
    }
  }, [])

  // Efecto para manejar cambios en filtros y ordenamiento
  useEffect(() => {
    if (currentFilters.length > 0) {
      requestDispatchOts(currentFilters, 0, pagination.pageSize)
    }
  }, [orderBy])

  return (
    <Fragment>
      <StandardPage pageTitle='Despachar OT'>
        {/* SEARCH */}
        <DispatchOtNewSearch
          key={searchKey}
          onSearchClick={handleSearch}
          onClear={handleClearSearch}
        />

        {state.data?.id && <OtDescription otDescription={state.data} />}

        {/* BUTTON BAR */}
        {state.data?.id && (
          <DispatchOtButtonBar
            onDeclareError={handleOpenDeclareError}
            onPrintManifest={() =>
              handlePrintDispatchManifest(state.data?.id || 0)
            }
            onDispatch={handleOpenDispatch}
            disabled={false}
            hasWorkOrder={(state.data?.id || 0) > 0}
          />
        )}

        {/* TABLE */}
        <GenericTableV2<DispatchOtDetail>
          data={state.data?.dispatchDetails || []}
          columns={tableColumns}
          keyField="skuId"
          loading={state.status === Status.Loading}
          styling={{
            dense: false,
            hoverEffect: true,
            rowStyle: (row) => {
              if (row.batch?.expirationDate && row.productOriginId) {
                const skuProductOriginDays =
                  productOrigins?.find(
                    (el) => el.productOriginId === row.productOriginId
                  )?.expirationDays || 0

                const status = getExpiredStatusToday(
                  row.batch.expirationDate,
                  skuProductOriginDays
                )
                return getStyleRow(status)
              }
              return {}
            }
          }}
          sorting={orderBy.field ? {
            field: orderBy.field,
            direction: orderBy.direction,
            onSortChange: handleSorting
          } : undefined}
          pagination={{
            page: pagination.page,
            pageSize: pagination.pageSize,
            total: state.data?.totalItems || 0,
            onPageChange: handlePaginationChange
          }}
          actions={[{
            key: 'incident',
            label: 'Reportar Incidente',
            icon: <EditIcon />,
            onClick: (row) => handleDispatchIncident(row.skuId, row)
          }]}
        />
      </StandardPage>

      {/* SNACKBAR */}
      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />

      {/* MODAL DECLARE ERROR */}
      <DeclareErrorModal
        isOpen={declareErrorModal.isOpen}
        onClose={declareErrorModal.closeModal}
        userId={userData?.userId || 0}
        workOrderId={state.data?.id || 0}
        showNotification={openSnackBar}
      />

      {/* MODAL UNIFICADO */}
      <UnifiedDispatchModalWrapper
        isOpen={unifiedDispatchModal.isOpen}
        onClose={unifiedDispatchModal.closeModal}
        selectedRow={unifiedDispatchModal.extendedProps?.selectedRow}
        typeInventoryIncident={typeInventoryIncident}
        workOrderId={unifiedDispatchModal.extendedProps?.workOrderId || 0}
        userId={unifiedDispatchModal.extendedProps?.userId || 0}
        showNotification={openSnackBar}
        onSuccess={() => handleGetDispatchOT()}
      />

      {/* MODAL BREAK STOCK */}
      <BreakStockModal
        isOpen={breakStockModal.isOpen}
        onClose={() => {
          breakStockModal.closeModal()
          handleGetDispatchOT()
        }}
      />

      {/* MODAL IMPRESIÓN MANIFIESTO */}
      <PrintManifestModal
        isOpen={printModal.isOpen}
        onClose={printModal.closeModal}
        workOrderId={printModal.extendedProps?.workOrderId}
        modalState={printModal.extendedProps}
        onStateChange={printModal.setExtendedProps}
      />

      {/* MODAL CONFIRMACIÓN DESPACHO */}
      <DispatchConfirmModal
        isOpen={dispatchModal.isOpen}
        onClose={dispatchModal.closeModal}
        onConfirm={() =>
          handleDispatchOT(dispatchModal.extendedProps?.workOrderId || 0)
        }
        workOrderId={dispatchModal.extendedProps?.workOrderId}
        isLoading={dispatchState.status === Status.Loading}
      />
    </Fragment>
  )
}
